import type { CSSProperties, ReactNode } from "react";
import { useState, useEffect } from "react";

import { MazeAppShell } from "@/components/layout/maze-app-shell";
import { MazeToaster } from "@/components/maze-toaster";
import { OfflineStatusModal } from "@/components/molecules/offline-status-modal";
import { AuthGateScreen } from "@/components/shared/AuthGateScreen";
import { useNetworkStatus } from "@/hooks/use-network-status";
import { useStageBootstrap } from "@/hooks/use-stage-bootstrap";
import { useDevAuth } from "@/hooks/use-dev-auth";
import { I18nProvider } from "@/lib/i18n/i18n-provider";
import { APP_COLOR } from "@/theme/color";
import { getConfiguredUiStageId } from "@/utils/stage/stage-utils";

interface AppProvidersProps {
  readonly children: ReactNode;
}

const bodyStyle: CSSProperties = {
  background: APP_COLOR.background,
  color: APP_COLOR.white,
  "--app-background": APP_COLOR.background,
  "--app-foreground": APP_COLOR.accent,
  "--game-accent-rgb": APP_COLOR.accentRgb,
} as CSSProperties;

function AppProvidersInner({ children }: AppProvidersProps) {
  const { isReady, error } = useDevAuth();
  const { isOnline } = useNetworkStatus();
  const [offlineModalOpen, setOfflineModalOpen] = useState(false);
  const stage = getConfiguredUiStageId();
  useStageBootstrap(isReady);

  useEffect(() => {
    if (!isOnline) {
      setOfflineModalOpen(true);
    }
  }, [isOnline]);

  const handleOfflineRetry = () => {
    if (!navigator.onLine) return;
    setOfflineModalOpen(false);
  };

  if (error) {
    return <AuthGateScreen gameThemeStyle={bodyStyle} error={error} />;
  }

  return (
    <MazeAppShell>
      {children}
      <MazeToaster />
      <OfflineStatusModal
        isOpen={offlineModalOpen}
        stage={stage}
        onRetry={handleOfflineRetry}
      />
    </MazeAppShell>
  );
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <I18nProvider>
      <div
        className="antialiased min-h-dvh w-full select-none"
        style={bodyStyle}
      >
        <AppProvidersInner>{children}</AppProvidersInner>
      </div>
    </I18nProvider>
  );
}
